// EditarDatosPerfil.js - Formulario para que el usuario edite SOLO nombre, teléfono, región y ciudad.
// El resto de los datos (RUT, apellido, email, fecha de nacimiento, etc.) solo los modifica un administrador.
import React, { useMemo, useState } from 'react';
import { REGIONES, getComunasPorRegion } from '../../data/regionesYComunas';
import { updateMyProfile } from '../../services/authService';

const TELEFONO_VALIDO = /^[0-9+\-() ]+$/;

const EditarDatosPerfil = ({ user, onGuardado, onCancelar }) => {
  const [form, setForm] = useState({
    nombre: user.nombre || '',
    telefono: user.telefono || '',
    region: user.region || '',
    ciudad: user.ciudad || '',
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // Si la región guardada no coincide exactamente con la lista, se agrega para no perderla
  const regiones = useMemo(
    () => (form.region && !REGIONES.includes(form.region) ? [form.region, ...REGIONES] : REGIONES),
    [form.region]
  );
  const comunas = useMemo(() => {
    const lista = form.region ? getComunasPorRegion(form.region) : [];
    return form.ciudad && !lista.includes(form.ciudad) ? [form.ciudad, ...lista] : lista;
  }, [form.region, form.ciudad]);

  const set = (campo) => (e) => {
    const valor = e.target.value;
    setForm((f) => (campo === 'region' ? { ...f, region: valor, ciudad: '' } : { ...f, [campo]: valor }));
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');
    const nombre = form.nombre.trim();
    const telefono = form.telefono.trim();
    if (!nombre) return setError('El nombre no puede quedar vacío');
    if (telefono && (!TELEFONO_VALIDO.test(telefono) || telefono.replace(/\D/g, '').length < 8)) {
      return setError('Teléfono no válido (ej: +56 9 1234 5678)');
    }

    // Solo se envían los campos que cambiaron
    const cambios = {};
    const nuevos = { nombre, telefono, region: form.region, ciudad: form.ciudad };
    Object.entries(nuevos).forEach(([k, v]) => {
      if ((v || '') !== (user[k] || '')) cambios[k] = v;
    });
    if (!Object.keys(cambios).length) {
      onCancelar?.();
      return;
    }

    setGuardando(true);
    try {
      const guardado = await updateMyProfile(cambios);
      onGuardado?.(guardado);
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form className="prof-edit" onSubmit={guardar} noValidate>
      <label className="prof-edit-field">
        <span>Nombre</span>
        <input type="text" value={form.nombre} onChange={set('nombre')} maxLength={100} autoComplete="given-name" required />
      </label>
      <label className="prof-edit-field">
        <span>Teléfono</span>
        <input type="tel" value={form.telefono} onChange={set('telefono')} maxLength={20} placeholder="+56 9 1234 5678" autoComplete="tel" />
      </label>
      <label className="prof-edit-field">
        <span>Región</span>
        <select value={form.region} onChange={set('region')}>
          <option value="">Selecciona una región</option>
          {regiones.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <label className="prof-edit-field">
        <span>Ciudad / comuna</span>
        <select value={form.ciudad} onChange={set('ciudad')} disabled={!form.region}>
          <option value="">{form.region ? 'Selecciona una comuna' : 'Primero elige una región'}</option>
          {comunas.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>

      <p className="prof-edit-note">
        Apellido, RUT, email y fecha de nacimiento solo los puede cambiar la administración de ACHIPO.
      </p>
      {error && <p className="prof-edit-error" role="alert">{error}</p>}

      <div className="prof-edit-actions">
        <button type="button" className="ach-pill ach-pill--sm prof-edit-cancel" onClick={onCancelar} disabled={guardando}>
          Cancelar
        </button>
        <button type="submit" className="ach-pill ach-pill--red ach-pill--sm" disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
};

export default EditarDatosPerfil;
